/**
 * Staff-facing card: the client's PERMANENT upload link.
 * Copy it, send it into their Telegram chat via the bot, or regenerate
 * it (revokes the old one) if it leaked. Shown in ClientDetail.
 */
import React, { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { copyText } from "../lib/clipboard";
import { Link2, Copy, Send, RefreshCw, ShieldAlert } from "lucide-react";

export default function UploadLinkCard({ clientId, telegramLinked }) {
  const [link, setLink] = useState(null); // { token, url }
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/staff/clients/${clientId}/upload-link`);
        setLink(data);
      } catch (e) {
        // non-blocking: card shows error state
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId]);

  const copyLink = async () => {
    if (!link?.url) return;
    const ok = await copyText(link.url);
    if (ok) toast.success("Link copiato negli appunti ✓");
    else window.prompt("Copia il link:", link.url);
  };

  const sendTelegram = async () => {
    setSending(true);
    try {
      await api.post(`/staff/clients/${clientId}/upload-link/send-telegram`);
      toast.success("Link inviato nella chat Telegram del cliente ✓");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const regenerate = async () => {
    const ok = window.confirm(
      "Rigenerare il link? Quello attuale smette SUBITO di funzionare " +
      "(usa questa opzione solo se il link è finito in mani sbagliate). " +
      "Dovrai reinviare il nuovo link al cliente."
    );
    if (!ok) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(
        `/staff/clients/${clientId}/upload-link/regenerate`
      );
      setLink(data);
      toast.success("Nuovo link generato — il vecchio è stato revocato");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div
      data-testid="client-upload-link-card"
      className="rounded-3xl border border-[var(--mp-border)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
    >
      <div className="space-y-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
          <Link2 className="h-3.5 w-3.5" /> Link upload video · fisso
        </div>
        <div className="font-display text-xl tracking-tight">
          Il canale diretto per i video del cliente
        </div>
        <p className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
          Link personale <strong>sempre valido</strong>, senza limiti di
          dimensione: il cliente lo apre, sceglie i video e questi finiscono
          nella sua cartella Dropbox + in Produzione (raggruppati per
          sessione). Consiglia al cliente di fissarlo in alto nella chat
          Telegram.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--mp-muted)]">Caricamento…</div>
      ) : !link?.url ? (
        <div className="text-sm text-red-700">
          Impossibile generare il link (URL portale non configurato).
        </div>
      ) : (
        <>
          <div
            data-testid="client-upload-link-url"
            className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] px-4 py-3 font-mono text-xs break-all select-all"
          >
            {link.url}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
            <Button
              data-testid="client-upload-link-copy-btn"
              onClick={copyLink}
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copia link
            </Button>
            <Button
              data-testid="client-upload-link-telegram-btn"
              onClick={sendTelegram}
              disabled={sending || !telegramLinked}
              variant="outline"
              className="rounded-full border-[var(--mp-ink)]"
              title={
                telegramLinked
                  ? "Invia il link nella chat Telegram del cliente"
                  : "Il cliente non ha ancora collegato Telegram"
              }
            >
              <Send className={`h-3.5 w-3.5 mr-1.5 ${sending ? "animate-pulse" : ""}`} />
              {sending ? "Invio…" : "Invia su Telegram"}
            </Button>
            <Button
              data-testid="client-upload-link-regenerate-btn"
              onClick={regenerate}
              disabled={regenerating}
              variant="outline"
              className="rounded-full border-red-400 text-red-700 hover:bg-red-50"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 mr-1.5 ${regenerating ? "animate-spin" : ""}`}
              />
              Rigenera
            </Button>
          </div>
          {!telegramLinked && (
            <div className="text-[11px] text-amber-800 flex items-center gap-1.5">
              <ShieldAlert className="h-3.5 w-3.5" />
              Telegram non collegato: puoi comunque copiare il link e
              mandarlo a mano.
            </div>
          )}
        </>
      )}
    </div>
  );
}
